import { useState } from 'react';
import type { DiscordActivityPendingBreakRequest, TesterDevice } from '@runhuman/shared';
import { formatTimeSinceCreated } from '../tester/harness';
import { formatBreakWindow } from '../../format-time';
import { BreakRequestDrawer } from './BreakRequestDrawer';

interface Props {
  readonly accessToken: string;
  readonly pendingBreakRequests: readonly DiscordActivityPendingBreakRequest[];
}

/**
 * Alert strip listing every pending break request waiting for admin action
 * (#4167, expanded for inline-resolve in #4201). Hidden entirely when the
 * list is empty — the strip's absence IS the all-clear signal (same
 * convention as `StuckJobsAlertStrip`).
 *
 * Each row's "Review" button toggles a `<BreakRequestDrawer>` mounted
 * directly beneath it. The drawer is the privacy-elevation point: the
 * snapshot row carries no alias or reason, but the drawer fetches the full
 * `AdminBreakRequestRow` (alias / reason / engine-suggested
 * `decisionOptions`) behind the same `requireAdmin: true` guard. Decide and
 * Escalate mutations run from inside the drawer through
 * `BreakEscalationService` — byte-identical to the web admin's
 * `/admin/schedules` flow.
 *
 * Escalation surfacing: rows with a non-null `escalatedAt` show a small
 * "Escalated" badge so admins can spot already-triggered rows at a glance.
 */
export function PendingBreakRequestsAlertStrip({ accessToken, pendingBreakRequests }: Props) {
  const [openRequestId, setOpenRequestId] = useState<string | null>(null);

  if (pendingBreakRequests.length === 0) return null;

  return (
    <section className="admin-pending-breaks" role="alert">
      <h2>Schedule requests ({pendingBreakRequests.length})</h2>
      <ul className="admin-pending-breaks__list">
        {pendingBreakRequests.map((request) => {
          const isOpen = openRequestId === request.requestId;
          return (
            <li key={request.requestId} className="admin-pending-breaks__row-wrapper">
              <div className="admin-pending-breaks__row">
                <div className="admin-pending-breaks__meta">
                  <span className="admin-pending-breaks__window">
                    {formatBreakWindow(request.startsAt, request.endsAt)}
                  </span>
                  <span className="admin-pending-breaks__origin">
                    {request.originator === 'admin' ? 'Admin-suggested' : 'Tester-requested'}
                  </span>
                  {request.escalatedAt && (
                    <span className="admin-pending-breaks__escalated">Escalated</span>
                  )}
                </div>
                <div className="admin-pending-breaks__devices">{formatDevices(request.devicesCovered)}</div>
                <div className="admin-pending-breaks__age">{formatTimeSinceCreated(request.createdAt)}</div>
                <button
                  type="button"
                  className="admin-pending-breaks__review"
                  aria-expanded={isOpen}
                  onClick={() =>
                    setOpenRequestId((current) => (current === request.requestId ? null : request.requestId))
                  }
                >
                  {isOpen ? 'Close' : 'Review'}
                </button>
              </div>
              {isOpen && (
                <BreakRequestDrawer
                  accessToken={accessToken}
                  requestId={request.requestId}
                  onClose={() => setOpenRequestId(null)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const DEVICE_LABEL: Record<TesterDevice, string> = {
  ios: 'iOS',
  android: 'Android',
  pc: 'PC',
  mac: 'Mac',
};

function formatDevices(devices: readonly TesterDevice[]): string {
  if (devices.length === 0) return 'No devices';
  return devices.map((d) => DEVICE_LABEL[d]).join(', ');
}
